/**
 * siteTrack.js — v145.1
 *
 * Fire-and-forget analytics ping helper for the marketing site + public
 * pages. Never blocks the UI: uses `sendBeacon` when the browser supports
 * it (survives page-unload), falls back to a `fetch(..., {keepalive: true})`
 * so navigation events like "click Sign in → route change" still land.
 *
 * We deliberately DO NOT depend on the authed `api` axios client — this
 * hits the public POST /api/site-events endpoint and should keep working
 * for logged-out visitors. Auth cookies are sent when present (harmless).
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = BACKEND ? `${BACKEND}/api/site-events` : null;

const ALLOWED_EVENTS = new Set([
  "landing_view",
  "signin_click",
  "signup_click",
  "trending_view",
  "trending_click",
  "action_hub_cta",
  "marketplace_cta",
  // v146.77 — Vertical landing pages (/for/{vertical}) fire these so
  // the admin dashboard can attribute signups back to the vertical.
  "vertical_view",
  "vertical_cta_click",
]);

// Client-side dedupe — a single mount firing landing_view twice (React
// StrictMode in dev) shouldn't inflate the metric. We keep an in-page
// memory of view events already fired.
const _firedViews = new Set();

export function trackSiteEvent(eventType, meta = {}) {
  if (!ENDPOINT) return;
  if (!ALLOWED_EVENTS.has(eventType)) return;
  // Dedupe view events per page-load. Click events are always allowed
  // through — user might genuinely click "Sign in" more than once.
  // v146.77 — Dedupe by (event, key-fields) so a user who navigates
  // between /for/doctors → /for/hotels gets one `vertical_view` per
  // vertical (not just the first). The dedupe still blocks React
  // StrictMode double-mounts on the same route.
  if (eventType.endsWith("_view")) {
    const dedupeKey = `${eventType}|${meta?.vertical || ""}|${meta?.utm_source || ""}`;
    if (_firedViews.has(dedupeKey)) return;
    _firedViews.add(dedupeKey);
  }
  const body = JSON.stringify({ event_type: eventType, meta: meta || {} });
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    }
  } catch { /* fall through to fetch */ }
  try {
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      keepalive: true,
    }).catch(() => {});
  } catch { /* swallow — analytics must never crash the app */ }
}
